import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../hooks/useToast";
import { useOnboarding } from "./OnboardingProvider";

/**
 * The CV preview's Continue, shared by `StepComplete.tsx` and
 * `StepComplete.web.tsx` (the twins only render).
 *
 * It goes straight on to who-sees-cv through the provider's `nextStep`
 * (which keeps the save/error handling and the privacy -> survey -> paywall
 * order). Nothing interrupts it any more: the feedback, store-review and
 * "get the app" pop-ups that used to sit here left onboarding (the user's
 * own CV is the payoff, and the asks come later, in the app). `busy` guards
 * the double tap and dims the dock while the step advances.
 */
export function useOnboardingPreviewContinue() {
  const { t } = useTranslation("auth");
  const toast = useToast();
  const { nextStep } = useOnboarding();
  const [busy, setBusy] = useState(false);
  const advancing = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const onContinue = useCallback(async () => {
    if (advancing.current) return;
    advancing.current = true;
    setBusy(true);
    try {
      await nextStep();
    } catch {
      // The provider handles save failures itself (toast + analytics); this
      // only protects the CTA from an unexpected navigation failure.
      if (mounted.current) {
        toast.error(
          t("onboarding.complete.continueError", {
            defaultValue: "Could not continue. Please try again.",
          }),
        );
      }
    } finally {
      advancing.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [nextStep, t, toast]);

  return { busy, onContinue };
}
